import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { geoAPI } from '../lib/api';
import { useUserLocation } from '../hooks/useUserLocation';
import { LocationMap } from '../components/LocationMap';
import { SiteFooter } from '../components/SiteFooter';
import { AIAssistant } from '../components/AIAssistant';
import {
  MapPin,
  Navigation,
  Loader2,
  AlertCircle,
  ChevronLeft,
  Store as StoreIcon,
  Phone,
  MessageCircle,
} from 'lucide-react';

const RADIUS_OPTIONS = [5, 10, 25, 50, 100, 200];

export const NearbyStores = () => {
  const { position, status, request } = useUserLocation(true);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(50);
  const [hoveredId, setHoveredId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!position) return;
    setLoading(true);
    geoAPI
      .nearby(position.lat, position.lng, radius)
      .then(({ data }) => setStores(data.stores || []))
      .catch(() => setStores([]))
      .finally(() => setLoading(false));
  }, [position, radius]);

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-gradient-to-br from-blue-600 to-violet-600 text-white">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
            data-testid="back-btn"
            aria-label="Voltar"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <Navigation className="w-5 h-5" />
              Lojas perto de você
            </h1>
            <p className="text-xs text-blue-100">
              {position
                ? `${stores.length} loja${stores.length === 1 ? '' : 's'} em até ${radius} km`
                : 'Compartilhe sua localização para ver lojas próximas'}
            </p>
          </div>
        </div>

        {/* Radius selector */}
        {position && (
          <div className="px-4 pb-3 overflow-x-auto">
            <div className="flex gap-2 min-w-max">
              {RADIUS_OPTIONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setRadius(r)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${
                    radius === r
                      ? 'bg-white text-blue-700'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                  data-testid={`radius-${r}`}
                >
                  até {r} km
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-5 space-y-5 max-w-6xl mx-auto">
        {/* Permission states */}
        {status === 'idle' && (
          <div className="text-center bg-white rounded-2xl border border-slate-200 p-8" data-testid="state-idle">
            <Loader2 className="w-7 h-7 text-blue-600 animate-spin mx-auto" />
            <p className="text-sm text-slate-500 mt-3">Solicitando localização…</p>
          </div>
        )}

        {status === 'requesting' && (
          <div className="text-center bg-white rounded-2xl border border-slate-200 p-8" data-testid="state-requesting">
            <Loader2 className="w-7 h-7 text-blue-600 animate-spin mx-auto" />
            <p className="text-sm text-slate-500 mt-3">Aguardando você autorizar o navegador…</p>
          </div>
        )}

        {(status === 'denied' || status === 'unavailable') && (
          <div className="text-center bg-amber-50 border border-amber-200 rounded-2xl p-8" data-testid="state-denied">
            <AlertCircle className="w-10 h-10 text-amber-600 mx-auto" />
            <p className="font-semibold text-slate-900 mt-3">Localização não disponível</p>
            <p className="text-sm text-slate-600 mt-1">
              Sem problemas — você pode navegar normalmente. Para ver lojas próximas,
              autorize a localização nas configurações do navegador.
            </p>
            <button
              onClick={request}
              className="mt-4 px-4 py-2 rounded-full bg-blue-600 text-white text-sm hover:bg-blue-700"
              data-testid="retry-location"
            >
              Tentar novamente
            </button>
            <div className="mt-3">
              <Link to="/" className="text-sm text-blue-600 hover:underline">Voltar para o marketplace</Link>
            </div>
          </div>
        )}

        {position && (
          <>
            {/* Map */}
            <LocationMap
              userPosition={position}
              stores={stores}
              readonly
              height="380px"
              testId="nearby-map"
            />

            {/* Stores list */}
            {loading ? (
              <div className="text-center py-10">
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin mx-auto" />
              </div>
            ) : stores.length === 0 ? (
              <div className="text-center bg-white border border-slate-200 rounded-2xl p-8" data-testid="state-empty">
                <StoreIcon className="w-10 h-10 text-slate-300 mx-auto" />
                <p className="font-semibold text-slate-900 mt-3">Nenhuma loja em {radius} km</p>
                <p className="text-sm text-slate-500 mt-1">Aumente o raio de busca acima.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="nearby-list">
                {stores.map((s) => (
                  <Link
                    key={s.id}
                    to={`/loja/${s.id}`}
                    onMouseEnter={() => setHoveredId(s.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    className={`bg-white rounded-2xl border transition-all p-4 flex gap-3 hover:shadow-lg ${
                      hoveredId === s.id ? 'border-blue-400 shadow-md' : 'border-slate-200'
                    }`}
                    data-testid={`nearby-card-${s.id}`}
                  >
                    {s.logo_url ? (
                      <img src={s.logo_url} alt={s.name} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-100 to-violet-100 flex items-center justify-center flex-shrink-0">
                        <StoreIcon className="w-6 h-6 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{s.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" />
                        {s.city}{s.state ? ` / ${s.state}` : ''}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                          <Navigation className="w-3 h-3" /> {s.distance_km} km
                        </span>
                        {s.whatsapp && (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <MessageCircle className="w-3 h-3" /> WhatsApp
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <SiteFooter />
      <AIAssistant />
    </div>
  );
};

export default NearbyStores;
